import React, { FC, useCallback, useEffect, useState } from 'react';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { CircularProgress, Dialog, DialogActions, Typography } from '@material-ui/core';

import { trajectorySectionCodeLookup } from 'src/config/api/models/Trajectory';
import { setShowTrajectoryPrintPdfSlice } from 'src/modules/shared/slices/print/slice';
import theme from 'src/config/theme';
import {
  ErrorStyledDiv,
  StyledButton,
  StyledDialogContent,
  StyledDialogTitle,
  StyledLink,
} from './Atoms';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useGetTrajectoryToPrintPDFQuery } from 'src/modules/shared/slices/print/entity';

const PrintPdf: FC = () => {
  const dispatch = useAppDispatch();
  const log = useDataLogging();
  const { currentTrajectoryId, currentSectionCode } = useAppSelector((state) => state.trajectories);
  const [shouldOpenNewTab, setShouldOpenNewTab] = useState<boolean>(true);

  const { data, isSuccess, isError, isLoading } = useGetTrajectoryToPrintPDFQuery({
    trajectoryId: currentTrajectoryId!,
    pageType: trajectorySectionCodeLookup[currentSectionCode!],
  });

  const handleCloseDialog = useCallback(() => {
    dispatch(setShowTrajectoryPrintPdfSlice(false));
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess && data && shouldOpenNewTab) {
      log(LogActionTypes.PRINT_TRAJECTORY_SUCCESS, { trajectoryId: currentTrajectoryId });
      window.open(data, '_blank')?.focus();
      handleCloseDialog();
    }
  }, [isSuccess, data, shouldOpenNewTab, handleCloseDialog, currentTrajectoryId, log]);

  useEffect(() => {
    if (isError) {
      log(LogActionTypes.PRINT_TRAJECTORY_ERROR, { trajectoryId: currentTrajectoryId });
    }
  }, [currentTrajectoryId, log, isError]);

  return (
    <Dialog
      style={{ minWidth: '350px' }}
      open={true}
      onClose={() => handleCloseDialog()}
      aria-describedby="alert-dialog-slide-description"
    >
      <StyledDialogTitle>
        <FontAwesomeSpriteIcon
          icon={SpriteIconsLight.CANCEL}
          iconSize="2xl"
          style={{ color: 'gray', cursor: 'pointer' }}
          onClick={() => handleCloseDialog()}
        />
      </StyledDialogTitle>
      <StyledDialogContent>
        {isError && (
          <>
            <ErrorStyledDiv>
              <FontAwesomeSpriteIcon
                icon={SpriteIconsLight.FILE}
                iconSize="2x"
                style={{ position: 'absolute', color: '#000000', transform: 'translate(-50%, 0)' }}
              ></FontAwesomeSpriteIcon>
              <FontAwesomeSpriteIcon
                icon={SpriteIconsLight.GHOST}
                iconSize="1x"
                style={{
                  color: '#000000',
                  position: 'absolute',
                  top: '25%',
                  transform: 'translate(-50%, 0)',
                }}
              />
            </ErrorStyledDiv>
            <Typography style={{ color: '#000000' }}>
              {'Oh ow... er is iets misgegaan bij het maken van de pdf. '}
              <StyledLink href="https://www.lernova.be/contact" target="_blank">
                {'Stuur ons een bericht'}
              </StyledLink>
              {' als dit zich blijft voordoen.'}
            </Typography>
          </>
        )}
        {isLoading && (
          <>
            <CircularProgress />
            <Typography style={{ color: '#000000' }}>
              We maken een nette pdf van deze pagina. Even geduld...
            </Typography>
          </>
        )}
      </StyledDialogContent>
      <DialogActions>
        <StyledButton
          style={{}}
          onClick={() => {
            setShouldOpenNewTab(false);
            handleCloseDialog();
          }}
        >
          <Typography style={{ color: theme.palette.secondary.main }}>Annuleer</Typography>
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
};

export default PrintPdf;
